// Wartung anlegen oder ändern (Konzept Abschnitt 9): Scope, Modus, Zeitraum,
// DE/EN-Text, Checkbox "Auf Statuspage veröffentlichen", Vorschau.
import { useState } from 'react';

import { fromLocalInput, PRODUCTS, slug, toLocalInput } from './api.ts';
import type { Api, EntryType, Maintenance, Mode, ProductId, Scope, Text } from './api.ts';

type Props = {
  api: Api;
  version: number;
  roles: string[];
  existing?: Maintenance;
  onDone: (msg: string) => void;
  onCancel: () => void;
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const MODE_LABEL: Record<Mode, string> = {
  banner: 'Hinweis (banner)',
  readonly: 'Nur lesen (readonly)',
  blocked: 'Gesperrt (blocked)',
};
const TYPE_LABEL: Record<EntryType, string> = { maintenance: 'Wartung', incident: 'Störung', announcement: 'Ankündigung' };

export function MaintenanceForm({ api, version, roles, existing, onDone, onCancel }: Props) {
  const now = new Date();
  const [shortName, setShortName] = useState('');
  const [type, setType] = useState<EntryType>(existing?.type ?? 'maintenance');
  const [mode, setMode] = useState<Mode>(existing?.mode ?? 'banner');
  const [scopeKind, setScopeKind] = useState<Scope['kind']>(existing?.scope.kind ?? 'products');
  const [products, setProducts] = useState<ProductId[]>(
    existing?.scope.kind === 'products'
      ? existing.scope.products
      : existing?.scope.kind === 'tenant'
        ? (existing.scope.products ?? [])
        : [],
  );
  const [tenant, setTenant] = useState(existing?.scope.kind === 'tenant' ? existing.scope.tenant : '');
  const [starts, setStarts] = useState(toLocalInput(existing?.starts_at ?? now.toISOString()));
  const [ends, setEnds] = useState(toLocalInput(existing?.ends_at ?? new Date(now.getTime() + 2 * 3600_000).toISOString()));
  const [message, setMessage] = useState<Text>(existing?.message ?? { de: '', en: '' });
  const [isPublic, setPublic] = useState(existing?.public ?? false);
  const [title, setTitle] = useState<Text>(existing?.title ?? { de: '', en: '' });
  const [internalRef, setInternalRef] = useState(existing?.internal_ref ?? '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const id = existing?.id ?? `mnt-${starts.slice(0, 10)}-${slug(shortName) || 'eintrag'}`;
  const canGlobal = roles.includes('global');
  const canPublish = roles.includes('publisher');

  const toggle = (p: ProductId) => setProducts((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  function scope(): Scope {
    if (scopeKind === 'global') return { kind: 'global' };
    if (scopeKind === 'tenant')
      return products.length ? { kind: 'tenant', tenant: tenant.trim(), products } : { kind: 'tenant', tenant: tenant.trim() };
    return { kind: 'products', products };
  }

  function validate(): string | null {
    if (!existing && !slug(shortName)) return 'Kurzname fehlt (wird Teil der ID).';
    if (scopeKind === 'products' && !products.length) return 'Mindestens ein Produkt wählen.';
    if (scopeKind === 'tenant' && !UUID.test(tenant.trim())) return 'Tenant-UUID (v4) eingeben.';
    if (!message.de.trim() || !message.en.trim()) return 'Text auf Deutsch und Englisch eingeben.';
    if (fromLocalInput(starts) >= fromLocalInput(ends)) return 'Ende muss nach dem Beginn liegen.';
    if (isPublic && (!title.de.trim() || !title.en.trim())) return 'Für die Statuspage einen Titel auf Deutsch und Englisch eingeben.';
    return null;
  }

  async function save() {
    const problem = validate();
    if (problem) return setError(problem);
    setBusy(true);
    setError('');
    try {
      const body = {
        type,
        mode,
        scope: scope(),
        starts_at: fromLocalInput(starts),
        ends_at: fromLocalInput(ends),
        message: { de: message.de.trim(), en: message.en.trim() },
        status_url: 'https://status.tks.eu',
        public: isPublic && scopeKind !== 'tenant',
        ...(isPublic && scopeKind !== 'tenant' ? { title: { de: title.de.trim(), en: title.en.trim() } } : {}),
        ...(internalRef.trim() ? { internal_ref: internalRef.trim() } : {}),
      };
      const r = await api.putMaintenance(id, body, version);
      onDone(
        !r.changed
          ? 'Keine Änderung.'
          : scopeKind === 'global'
            ? `${id} gespeichert. Wirksam erst nach Freigabe durch eine zweite Person.`
            : `${id} gespeichert${r.published ? ' und veröffentlicht' : ', Veröffentlichung folgt mit dem nächsten Heartbeat'}.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>{existing ? `Wartung ${existing.id} bearbeiten` : 'Neuer Eintrag'}</h2>
      {!existing && (
        <label>
          Kurzname (für die ID)
          <input value={shortName} onChange={(e) => setShortName(e.target.value)} placeholder="z. B. planung-db" />
          <small>ID: {id}</small>
        </label>
      )}
      <div className="row">
        <label>
          Anlass
          <select value={type} onChange={(e) => setType(e.target.value as EntryType)}>
            {Object.entries(TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
        <label>
          Verhalten der Apps
          <select value={mode} onChange={(e) => setMode(e.target.value as Mode)}>
            {Object.entries(MODE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </label>
      </div>

      <fieldset>
        <legend>Wer ist betroffen?</legend>
        <label className="inline">
          <input type="radio" checked={scopeKind === 'products'} onChange={() => setScopeKind('products')} /> Produkte
        </label>
        <label className="inline">
          <input type="radio" checked={scopeKind === 'tenant'} onChange={() => setScopeKind('tenant')} /> ein Tenant
        </label>
        <label className="inline">
          <input type="radio" disabled={!canGlobal} checked={scopeKind === 'global'} onChange={() => setScopeKind('global')} /> alle
          (global
          {canGlobal ? ', Vier-Augen-Freigabe' : ', Rolle status-global nötig'})
        </label>
        {scopeKind === 'tenant' && (
          <label>
            Tenant-UUID
            <input value={tenant} onChange={(e) => setTenant(e.target.value)} placeholder="xxxxxxxx-xxxx-4xxx-xxxx-xxxxxxxxxxxx" />
          </label>
        )}
        {scopeKind !== 'global' && (
          <div className="chips">
            {PRODUCTS.map((p) => (
              <label key={p.id} className="inline">
                <input type="checkbox" checked={products.includes(p.id)} onChange={() => toggle(p.id)} /> {p.name}
              </label>
            ))}
            {scopeKind === 'tenant' && <small>Keine Auswahl = alle Produkte des Tenants.</small>}
          </div>
        )}
        {scopeKind === 'products' && products.includes('tk-login') && (
          <small>Einträge für TK-Login erscheinen in allen Produkten als Hinweis.</small>
        )}
      </fieldset>

      <div className="row">
        <label>
          Beginn (deutsche Zeit)
          <input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
        </label>
        <label>
          Ende
          <input type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} />
        </label>
      </div>

      <label>
        Text in den Apps (Deutsch)
        <textarea value={message.de} onChange={(e) => setMessage({ ...message, de: e.target.value })} rows={2} />
      </label>
      <label>
        Text in den Apps (Englisch)
        <textarea value={message.en} onChange={(e) => setMessage({ ...message, en: e.target.value })} rows={2} />
      </label>

      <label className="inline">
        <input
          type="checkbox"
          checked={isPublic && scopeKind !== 'tenant'}
          disabled={!canPublish || scopeKind === 'tenant'}
          onChange={(e) => setPublic(e.target.checked)}
        />{' '}
        Auf Statuspage veröffentlichen {!canPublish && '(Rolle status-publisher nötig)'}
        {scopeKind === 'tenant' && '(Tenant-Einträge bleiben privat)'}
      </label>
      {isPublic && scopeKind !== 'tenant' && (
        <div className="row">
          <label>
            Titel auf der Statuspage (DE)
            <input value={title.de} onChange={(e) => setTitle({ ...title, de: e.target.value })} />
          </label>
          <label>
            Titel (EN)
            <input value={title.en} onChange={(e) => setTitle({ ...title, en: e.target.value })} />
          </label>
        </div>
      )}
      <label>
        Interne Referenz (optional, nie öffentlich)
        <input value={internalRef} onChange={(e) => setInternalRef(e.target.value)} placeholder="z. B. Keep-Incident oder Ticket" />
      </label>

      <h3>Vorschau</h3>
      <div className={`banner banner-${mode}`}>
        <strong>
          {mode === 'blocked' ? 'Vollbild-Wartungsseite' : mode === 'readonly' ? 'Hinweis, Schreiben gesperrt' : 'Hinweis'}
        </strong>
        <p>{message.de || '…'}</p>
      </div>
      {isPublic && scopeKind !== 'tenant' && (
        <div className="statuspage-preview">
          <span className="pill">Geplant</span> <strong>{title.de || '…'}</strong>
          <p>
            {starts.replace('T', ' ')} – {ends.replace('T', ' ')}
          </p>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      <div className="actions">
        <button className="primary" disabled={busy} onClick={() => void save()}>
          Speichern
        </button>
        <button onClick={onCancel}>Abbrechen</button>
      </div>
    </section>
  );
}
